if (!customElements.get('kd-collection-card')) {
  customElements.define('kd-collection-card', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.data = JSON.parse(this.querySelector('[data-kd-card-data]').textContent);
      this.labels = this.data.labels;
      this.fields = [...this.querySelectorAll('fieldset')];
      this.selected = this.fields.map(field => field.querySelectorAll('button').length === 1 ? field.querySelector('button').dataset.optionValue : null);
      this.form = this.querySelector('form');
      this.cartAction = this.querySelector('.kd-card__cart');
      this.cartActionLabel = this.cartAction.querySelector('span');
      this.dialogTrigger = this.querySelector('.kd-card__cart-dialog-trigger button');
      this.status = this.querySelector('[role=status]');
      this.baseUrl = this.querySelector('[data-product-url]').dataset.productUrl;
      this.fields.forEach((field, index) => field.querySelectorAll('button').forEach((button, originalOrder) => {
        button.dataset.originalOrder = String(originalOrder);
        const raw = button.dataset.optionValue;
        const display = this.labels.colours?.[raw.toLowerCase()] || raw;
        button.querySelector('.kd-card__value').textContent = display;
        button.dataset.display = display;
        button.addEventListener('click', () => {
          if (button.disabled || this.busy) return;
          this.selected[index] = raw;
          this.fields.forEach((other, nextIndex) => {
            if (nextIndex > index && other.querySelectorAll('button').length > 1) this.selected[nextIndex] = null;
          });
          this.status.textContent = '';
          const variant = this.data.variants.find(item => item.options.every((option, optionIndex) => !this.selected[optionIndex] || option === this.selected[optionIndex]));
          const image = button.dataset.cardImageUrl || variant?.featured_image?.src;
          if (image) {
            const primary = this.querySelector('.kd-card__primary');
            primary.src = image;
            primary.removeAttribute('srcset');
            primary.alt = this.querySelector('h2').textContent + ' — ' + this.selected.filter(Boolean).join(' / ');
            const alternate = this.querySelector('.kd-card__alternate');
            if (alternate) alternate.hidden = true;
          }
          this.sync();
        });
      }));
      this.cartAction.addEventListener('click', () => this.requestAdd());
      this.querySelectorAll('.kd-card__options').forEach(rail => this.enableMouseDrag(rail));
      this.form.hidden = false;
      this.sync();
    }

    enableMouseDrag(rail) {
      let gesture;
      let suppressClick = false;
      rail.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        suppressClick = false;
        gesture = {id: event.pointerId, x: event.clientX, y: event.clientY, left: rail.scrollLeft, dragging: false};
      });
      rail.addEventListener('pointermove', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        if (!(event.buttons & 1)) {
          gesture = null;
          rail.classList.remove('is-dragging');
          return;
        }
        const distance = event.clientX - gesture.x;
        if (!gesture.dragging) {
          if (Math.abs(distance) < 6 || Math.abs(distance) < Math.abs(event.clientY - gesture.y)) return;
          if (rail.scrollWidth <= rail.clientWidth) return;
          gesture.dragging = true;
          suppressClick = true;
          rail.setPointerCapture(event.pointerId);
          rail.classList.add('is-dragging');
        }
        event.preventDefault();
        rail.scrollLeft = gesture.left - distance;
      });
      const finish = event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        gesture = null;
        rail.classList.remove('is-dragging');
        if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
      };
      rail.addEventListener('pointerup', finish);
      rail.addEventListener('pointercancel', finish);
      rail.addEventListener('lostpointercapture', finish);
      rail.addEventListener('click', event => {
        if (!suppressClick || event.detail === 0) return;
        suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, true);
      rail.addEventListener('dragstart', event => event.preventDefault());
    }

    sync() {
      this.fields.forEach((field, index) => {
        const choice = this.selected[index];
        const buttons = [...field.querySelectorAll('button')];
        const selectedButton = buttons.find(button => button.dataset.optionValue === choice);
        field.querySelector('.kd-card__choice').textContent = selectedButton ? ' · ' + selectedButton.dataset.display : '';
        buttons.forEach(button => {
          const value = button.dataset.optionValue;
          const available = this.data.variants.some(variant => variant.available && variant.options[index] === value && variant.options.every((option, optionIndex) => optionIndex >= index || !this.selected[optionIndex] || option === this.selected[optionIndex]));
          const awaiting = this.selected.slice(0, index).some(item => !item);
          button.disabled = this.busy || !available || awaiting;
          button.classList.toggle('is-sold-out', !available);
          button.hidden = !available;
          button.classList.toggle('hidden', !available);
          button.setAttribute('aria-pressed', String(choice === value));
          button.setAttribute('aria-label', button.dataset.display + (!available ? ' — ' + this.labels.sold_out : ''));
          button.title = button.getAttribute('aria-label');
        });
        buttons
          .sort((first, second) => {
            const availabilityOrder = Number(first.classList.contains('is-sold-out')) - Number(second.classList.contains('is-sold-out'));
            return availabilityOrder || Number(first.dataset.originalOrder) - Number(second.dataset.originalOrder);
          })
          .forEach(button => field.querySelector('.kd-card__options').append(button));
      });
      this.variant = this.data.defaultOnly ? this.data.variants[0] : this.data.variants.find(variant => variant.options.every((option, index) => option === this.selected[index]));
      const productAvailable = this.data.variants.some(variant => variant.available);
      const actionDisabled = this.busy || !productAvailable;
      this.cartAction.disabled = actionDisabled;
      const actionLabel = this.busy ? this.labels.adding : productAvailable ? this.labels.add_to_cart : this.labels.sold_out;
      this.cartActionLabel.textContent = actionLabel;
      const productUrl = new URL(this.baseUrl, window.location.origin);
      if (this.variant) productUrl.searchParams.set('variant', this.variant.id);
      this.querySelectorAll('[data-product-url]').forEach(opener => {
        opener.dataset.productUrl = productUrl.pathname + productUrl.search;
      });
      if (this.variant) {
        const price = this.querySelector('.kd-card__price');
        price.textContent = this.data.prices[this.variant.id];
        if (this.variant.compare_at_price > (this.data.displayPrices?.[this.variant.id] ?? this.variant.price)) {
          const compare = document.createElement('s');
          compare.textContent = this.data.comparePrices[this.variant.id];
          compare.style.cssText = 'font-weight:normal;opacity:.65;margin-inline-start:8px';
          price.append(compare);
        }
      }
    }

    requestAdd() {
      if (this.busy || !this.data.variants.some(variant => variant.available)) return;
      if (!this.variant?.available) {
        this.dialogTrigger.click();
        return;
      }
      this.submit();
    }

    resetUnavailableSelection() {
      this.selected.forEach((value, index) => {
        if (value && !this.data.variants.some(variant => variant.available && variant.options.every((option, position) => position > index || !this.selected[position] || option === this.selected[position]))) {
          this.selected.fill(null, index);
        }
      });
    }

    async refreshAvailability(rejectedId) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL(this.baseUrl, location.origin);
        url.pathname += '.js';
        url.search = '';
        const response = await fetch(url.href, {cache: 'no-store', signal: controller.signal});
        if (!response.ok) throw new Error();
        const product = await response.json();
        if (!Array.isArray(product.variants) || !product.variants.some(variant => String(variant.id) === String(rejectedId) && typeof variant.available === 'boolean')) throw new Error();
        this.data.variants.forEach(variant => {
          const current = product.variants.find(item => String(item.id) === String(variant.id));
          if (current && typeof current.available === 'boolean') variant.available = current.available;
        });
      } catch {
        const rejected = this.data.variants.find(variant => String(variant.id) === String(rejectedId));
        if (rejected) rejected.available = false;
      } finally {
        clearTimeout(timeout);
        this.resetUnavailableSelection();
      }
    }

    async submit() {
      if (this.busy || !this.variant?.available) return;
      const variantId = this.variant.id;
      const root = window.Shopify?.routes?.root || '/';
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cart?.getSectionsToRender?.().map(section => section.id) || [];
      this.busy = true;
      this.status.textContent = '';
      this.sync();
      let added = false;
      try {
        cart?.setActiveElement?.(this.cartAction);
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({items: [{id: variantId, quantity: 1}], sections: [...new Set(sections)], sections_url: location.pathname})
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          const error = new Error(result.description || result.message || this.labels.error);
          error.status = Number(result.status) || response.status;
          throw error;
        }
        added = true;
        this.status.textContent = this.labels.added;
        const item = result.items?.[0] || result;
        const drawerData = {...item, sections: result.sections};
        document.dispatchEvent(new CustomEvent('afterCartChanged', {detail: {productVariantId: String(variantId), source: 'product-form', cartData: drawerData}}));
        if (cart && sections.every(id => result.sections?.[id])) {
          cart.classList.remove('is-empty');
          cart.renderContents(drawerData);
        } else {
          location.assign(root + 'cart');
        }
      } catch (error) {
        if (added) location.assign(root + 'cart');
        else {
          this.status.textContent = error.message || this.labels.error;
          if (error.status === 422) await this.refreshAvailability(variantId);
        }
      } finally {
        this.busy = false;
        this.sync();
      }
    }
  });
}

